import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import _get from 'lodash/get';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { colors } from '../constants';
import {
  getThermometerInterval,
  normalizeIntervals,
  normalizeResult,
} from '../utils';

const useStyles = makeStyles(theme => ({
  wrapper: {
    paddingTop: ({ showCaption }) => (showCaption ? 20 : 0),
    paddingBottom: ({ showCaption }) => (showCaption ? 0 : 20),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
  },
  barWrapper: {
    flex: 1,
    marginLeft: 7,
    marginRight: 7,
  },
  labels: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    paddingBottom: 4,
  },
  bars: {
    height: 13,
    position: 'relative',
  },
  lightBar: {
    backgroundColor: colors.none,
    width: '100%',
    height: '100%',
    borderRadius: 4.5,
  },
  rulerMark: {
    lineHeight: '1',
  },
  currentBar: {
    position: 'absolute',
    backgroundColor: ({ fillColor }) => fillColor,
    width: ({ width }) => `calc(${width} + 4px)`,
    height: '100%',
    top: 0,
    left: ({ left }) => `calc(${left} - 2px)`,
    borderRadius: 6.5,
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: '#FFFFFF',
    boxShadow: ({ fillColor }) => `0 0 6px 0 ${fillColor}`,
  },
  bar: {
    height: '100%',
    position: 'absolute',
    top: 0,
    borderTop: '2px solid #FFFFFF',
    borderBottom: '2px solid #FFFFFF',
    borderLeft: '1px solid #FFFFFF',
    borderRight: '1px solid #FFFFFF',
  },
  label: {
    textAlign: 'center',
    display: 'inline-flex',
    justifyContent: 'center',
    paddingLeft: 2,
    paddingRight: 2,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.75rem',
      lineHeight: '1.2',
    },
  },
  current: {
    position: 'absolute',
    backgroundColor: 'transparent',
    width: 0,
    height: '100%',
    left: ({ point }) => point,
    top: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginTop: 8,
    marginBottom: 32,
  },
  captionWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 12,
  },
  caption: {
    fontFamily: 'PoppinsItalic',
    fontSize: 13,
  },
}));

const useTooltipStyles = makeStyles(theme =>
  createStyles({
    tooltip: {
      ...theme.typography.pSmallBold,
      backgroundColor: '#FFF',
      color: theme.palette.text.primary,
      border: `2px solid ${theme.palette.primary.secondary}`,
      borderRadius: 5,
      [theme.breakpoints.down('xs')]: {
        marginTop: 16,
      },
    },
    popper: {
      zIndex: 8,
      position: 'absolute',
      left: ({ point }) => point,
    },
    arrow: {
      '&::before': {
        border: `2px solid ${theme.palette.primary.secondary}`,
        backgroundColor: '#fff',
        boxSizing: 'border-box',
      },
    },
  }),
);

export default function HorizontalProgress({
  min,
  max,
  value,
  isThermometer,
  interval,
  intervals,
  scale,
  showCaption,
  isOverall,
  expression,
  label,
}) {
  const [assessmentResultsSiteCopy] = useSiteCopySelector([
    'assessment-results',
  ]);
  const normalizedIntervals = normalizeIntervals(
    intervals.sort((a, b) => a.start - b.start),
    min,
    max,
    scale,
  );
  const [
    normalizedValue,
    normalizedIntervalStart,
    normalizedIntervalEnd,
    maxValue,
  ] = normalizeResult(value, min, max, interval, scale);

  const finalIntervalStart = isThermometer ? 0 : normalizedIntervalStart;
  const finalIntervalEnd = isThermometer
    ? normalizedValue
    : normalizedIntervalEnd;
  const classes = useStyles({
    showCaption,
    fillColor: (interval && interval.color) || 'transparent',
    width: `${(finalIntervalEnd - finalIntervalStart) * (100 / maxValue)}%`,
    left: `${finalIntervalStart * (100 / maxValue)}%`,
    point: `${normalizedValue * (100 / maxValue)}%`,
  });
  const tooltipClasses = useTooltipStyles({
    point: `${
      normalizedValue * (100 / maxValue) === 100
        ? 88
        : normalizedValue * (100 / maxValue)
    }%`,
  });

  if (!interval) return null;

  const finalIntervals = [];
  if (isThermometer) {
    finalIntervals.push(getThermometerInterval(normalizedValue, scale));
  } else {
    finalIntervals.push(...normalizedIntervals);
  }

  return (
    <div className={classes.container}>
      <div className={classes.wrapper}>
        <Typography variant="overline" className={classes.rulerMark}>
          0
        </Typography>
        <div className={classes.barWrapper}>
          <div className={classes.labels}>
            {finalIntervals.map(el => (
              <Typography
                key={`${label}-${el.start}-label`}
                className={classes.label}
                variant="overline"
                component="p"
                style={{
                  left: `${el.start * (100 / maxValue)}%`,
                  width: `${(el.end - el.start) * (100 / maxValue)}%`,
                  fontWeight:
                    el.start !== normalizedIntervalStart ? 'normal' : 'bold',
                }}
              >
                {el.label}
              </Typography>
            ))}
          </div>
          <div className={classes.bars}>
            {finalIntervals.map((el, index) => (
              <div
                key={`${label}-${el.start}-bar`}
                className={classes.bar}
                style={{
                  backgroundColor: el.color,
                  width: `${(el.end - el.start) * (100 / maxValue)}%`,
                  left: `${el.start * (100 / maxValue)}%`,
                  borderTopLeftRadius: index === 0 && !isThermometer ? 6.5 : 0,
                  borderBottomLeftRadius:
                    index === 0 && !isThermometer ? 6.5 : 0,
                  borderTopRightRadius:
                    index === finalIntervals.length - 1 ? 6.5 : 0,
                  borderBottomRightRadius:
                    index === finalIntervals.length - 1 ? 6.5 : 0,
                }}
              />
            ))}
            {(!isThermometer || normalizedValue > 0) && (
              <div className={classes.currentBar} />
            )}
            <Tooltip
              classes={tooltipClasses}
              placement={showCaption ? 'top' : 'bottom'}
              open
              arrow
              title={`${_get(
                assessmentResultsSiteCopy,
                'pageCopy.horizontalProgressUserLabel',
              )} (${Math.round(normalizedValue)})`}
              PopperProps={{
                disablePortal: true,
                modifiers: { flip: { enabled: false } },
              }}
            >
              <div className={classes.current} />
            </Tooltip>
          </div>
        </div>
        <Typography variant="overline" className={classes.rulerMark}>
          {maxValue}
        </Typography>
      </div>
      {showCaption && (
        <div className={classes.captionWrapper}>
          <Typography variant="overline" className={classes.caption}>
            {`${
              isOverall
                ? `${_get(
                    assessmentResultsSiteCopy,
                    'pageCopy.horizontalProgressOverallLabel',
                  )}: `
                : ''
            }${expression} (${normalizedIntervalStart}-${normalizedIntervalEnd})`}
          </Typography>
        </div>
      )}
    </div>
  );
}

HorizontalProgress.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  value: PropTypes.number,
  scale: PropTypes.number,
  interval: PropTypes.object,
  intervals: PropTypes.array,
  expression: PropTypes.string,
  label: PropTypes.string,
  showCaption: PropTypes.bool,
  isOverall: PropTypes.bool,
};
